// ./src/pages/private/EventGuestGroupsPage/GuestGroupDrawer.tsx
//
// Add / edit one group within a guest-group set ("Team 1", "VIP"). Fields: name,
// description, an optional badge color + icon, and an optional member cap. Opened
// (stacked) from the groups drawer. Uses the shared AppDrawer + form fields.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { OpResult } from '@sdflc/api-helpers';

import { GuestGroup, GuestGroupInput, GuestGroupService } from '@/utils/api';
import { getFormErrors, formatErrors } from '@/utils';
import { useToast, useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from '@/hooks';
import { AppDrawer, TextField, TextAreaField, NumberField, IconSelector, ColorPickerInput } from '@/components/generic';


// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
const KNOWN_FORM_FIELDS = ['name', 'description', 'color', 'icon', 'maxMembers', 'bibPrefix', 'bibStartNumber'];

interface GroupForm {
  name: string;
  description: string;
  color: string;
  icon: string | null;
  maxMembers: number | null;
  bibPrefix: string;
  bibStartNumber: string;
}

const emptyForm = (): GroupForm => ({ name: '', description: '', color: '', icon: null, maxMembers: null, bibPrefix: '', bibStartNumber: '' });

const toForm = (g: GuestGroup): GroupForm => ({
  name: g.name ?? '',
  description: g.description ?? '',
  color: g.color ?? '',
  icon: g.icon ?? null,
  maxMembers: g.maxMembers ?? null,
  bibPrefix: g.bibPrefix ?? '',
  bibStartNumber: g.bibStartNumber ?? '',
});

export interface GuestGroupDrawerProps {
  opened: boolean;
  onClose: () => void;
  guestGroupSetId: string;
  group: GuestGroup | null;
  /** order_no to assign a newly-created value (append at the end). */
  defaultOrderNo: number;
  onSaved: () => void;
  /** Called with the created group — lets a picker select what was just added. */
  onCreated?: (group: GuestGroup) => void;
  /** Show BIB config fields. Hidden for ticketed events (bib lives on the ticket). */
  bibEnabled?: boolean;
}

export const GuestGroupDrawer: React.FC<GuestGroupDrawerProps> = ({ opened, onClose, guestGroupSetId, group, defaultOrderNo, onSaved, onCreated, bibEnabled = true }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const [form, setForm] = useState<GroupForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );

  // Esc / a click outside / Cancel ask before discarding a changed group (#123).
  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    // A refreshed group keeps what the user has typed (#125); another group replaces the form.
    const seeded = group ? toForm(group) : emptyForm();
    setForm(reseed(seeded, group?.id ?? "__new__"));
    setSubmitError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, group]);

  useEffect(() => {
    if (!opened) return undefined;
    const id = setTimeout(() => nameRef.current?.focus(), 120);
    return () => clearTimeout(id);
  }, [opened]);

  const handleChange = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const canSave = !!form.name.trim() && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);

    const input: GuestGroupInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      color: form.color || null,
      icon: form.icon,
      maxMembers: form.maxMembers,
      bibPrefix: form.bibPrefix.trim() || null,
      bibStartNumber: form.bibStartNumber.trim() || null,
    };

    const result = group?.id
      ? await GuestGroupService.update(group.id, input)
      : await GuestGroupService.create({ ...input, guestGroupSetId, orderNo: defaultOrderNo });

    setSaving(false);
    if (!result.didSucceed()) {
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }
    toast.success(t('events:guestGroups.groupSaved', { defaultValue: 'Group saved.' }));

    if (!group?.id) {
      onCreated?.(result.getDataFirst() as GuestGroup);
    }

    onSaved();
    onClose();
  };

  useModalShortcuts({ onConfirm: save, canConfirm: canSave, enabled: opened });

  const title = group
    ? t('events:guestGroups.editGroupTitle', { defaultValue: 'Edit group' })
    : t('events:guestGroups.addGroupTitle', { defaultValue: 'Add group' });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          ref={nameRef}
          name="name"
          label={t('events:guestGroups.groupName', { defaultValue: 'Group name' })}
          placeholder={t('events:guestGroups.groupNamePh', { defaultValue: 'e.g. Team 1 / VIP' })}
          value={form.name}
          onChange={handleChange}
          error={fieldErrors.name}
          required
        />

        <TextAreaField
          name="description"
          label={t('events:guestGroups.description', { defaultValue: 'Description' })}
          value={form.description}
          onChange={handleChange}
          error={fieldErrors.description}
          autosize
          minRows={2}
          maxRows={4}
        />

        <Group gap="md" grow align="flex-start">
          <ColorPickerInput
            label={t('events:guestGroups.color', { defaultValue: 'Color' })}
            value={form.color}
            onChange={(_n, v) => setForm((f) => ({ ...f, color: v }))}
            error={fieldErrors.color}
          />
          <IconSelector
            label={t('events:guestGroups.icon', { defaultValue: 'Icon' })}
            value={form.icon}
            onChange={(icon) => setForm((f) => ({ ...f, icon }))}
            error={fieldErrors.icon}
          />
        </Group>

        <NumberField
          name="maxMembers"
          label={t('events:guestGroups.maxMembers', { defaultValue: 'Member cap' })}
          description={t('events:guestGroups.maxMembersHint', { defaultValue: 'Optional — the most guests allowed in this group. Leave empty for unlimited.' })}
          value={form.maxMembers}
          onChange={handleChange}
          error={fieldErrors.maxMembers}
          min={1}
          allowDecimal={false}
          clampBehavior="strict"
          placeholder={t('events:guestGroups.unlimited', { defaultValue: 'Unlimited' })}
        />

        {bibEnabled && (
          <>
            <Divider
              label={t('events:guestGroups.bibSection', { defaultValue: 'BIB numbers (sport events)' })}
              labelPosition="left"
            />

            <Group gap="md" grow align="flex-start">
              <TextField
                name="bibPrefix"
                label={t('events:guestGroups.bibPrefix', { defaultValue: 'BIB prefix' })}
                placeholder={t('events:guestGroups.bibPrefixPh', { defaultValue: 'e.g. MSP, 5K' })}
                value={form.bibPrefix}
                onChange={handleChange}
                error={fieldErrors.bibPrefix}
                maxLength={32}
              />
              <TextField
                name="bibStartNumber"
                label={t('events:guestGroups.bibStart', { defaultValue: 'Start number' })}
                description={t('events:guestGroups.bibStartHint', { defaultValue: 'Leading zeros set the digit width, e.g. 0001.' })}
                placeholder="0001"
                value={form.bibStartNumber}
                onChange={handleChange}
                error={fieldErrors.bibStartNumber}
                maxLength={16}
              />
            </Group>
          </>
        )}
      </Stack>
    </AppDrawer>
  );
};
